<template>
  <div class="help-page">
    <div class="container-fluid py-4">
      <!-- Top header bar -->
      <header class="hp-header mb-3">
        <div class="d-flex justify-content-between align-items-end flex-wrap gap-2">
          <div>
            <h1 class="hp-title mb-1">Help · User Guide</h1>
            <p class="hp-subtitle mb-0">
              Presets → Build Pipeline → Bind Files → Run Task (recommended to start from presets).
            </p>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" @click="goStore">Preset Store</button>
            <button class="btn btn-sm btn-outline-primary" @click="goBatch">Batch Builder</button>
          </div>
        </div>

        <div class="hp-hintbar mt-2 d-flex flex-wrap align-items-center gap-2">
          <span class="hp-hint-badge">Tips</span>
          <span class="hp-hint-text">
            Step 1 locks the input structure. If you need to change input types or quantities, please create a new task.
          </span>
        </div>
      </header>

      <div class="row gx-3 gy-3">
        <!-- Left navigation (lightweight) -->
        <aside class="col-12 col-lg-3">
          <section class="hp-card hp-card--sticky">
            <div class="hp-card-header">
              <div class="hp-card-title-sm">Contents</div>
              <div class="hp-card-caption">Quick Navigation</div>
            </div>

            <div class="hp-nav">
              <a class="hp-nav-item" href="#overview">1. Overview</a>
              <a class="hp-nav-item" href="#quickstart">2. Quick Start</a>
              <a class="hp-nav-item" href="#concepts">3. Core Concepts</a>
              <a class="hp-nav-item" href="#faq">4. FAQ & Notes</a>
            </div>

            <div class="hp-mini mt-3">
              <div class="hp-mini-title">One-Line Version</div>
              <div class="hp-mini-text">Define inputs → Assemble steps → Bind files → Run</div>
            </div>
          </section>
        </aside>

        <!-- Right content -->
        <main class="col-12 col-lg-9">
          <!-- 1 Overview -->
          <section id="overview" class="hp-card mb-3">
            <div class="hp-card-header">
              <div>
                <div class="hp-card-title-sm">1 · Overview</div>
                <div class="hp-card-caption">You only need to understand these four things</div>
              </div>
            </div>

            <div class="hp-grid4">
              <div class="hp-tile">
                <div class="hp-tile-title">Preset</div>
                <div class="hp-tile-text">Choose a pipeline skeleton to reduce the cost of building from scratch.</div>
              </div>
              <div class="hp-tile">
                <div class="hp-tile-title">Step 1 · Inputs</div>
                <div class="hp-tile-text">Declare types and quantities; once confirmed, they are locked.</div>
              </div>
              <div class="hp-tile">
                <div class="hp-tile-title">Step 2 · Steps</div>
                <div class="hp-tile-text">Add software steps and configure parameters and indices.</div>
              </div>
              <div class="hp-tile">
                <div class="hp-tile-title">Step 3 · Binding</div>
                <div class="hp-tile-text">Bind file sources by sample group and then run the task.</div>
              </div>
            </div>
          </section>

          <!-- 2 Quick Start -->
          <section id="quickstart" class="hp-card mb-3">
            <div class="hp-card-header">
              <div>
                <div class="hp-card-title-sm">2 · Quick Start</div>
                <div class="hp-card-caption">Get up and running in three steps</div>
              </div>
            </div>

            <div class="hp-steps">
              <div class="hp-step">
                <div class="hp-step-badge">Step 1</div>
                <div class="hp-step-main">
                  <div class="hp-step-title">Configure Inputs</div>
                  <div class="hp-step-text">
                    Select the input types and quantities required for this run (e.g., fq / fq1 / fq2 / bam / bed).
                    After confirmation, the binding structure is generated automatically.
                  </div>
                </div>
              </div>

              <div class="hp-step">
                <div class="hp-step-badge">Step 2</div>
                <div class="hp-step-main">
                  <div class="hp-step-title">Build Pipeline</div>
                  <div class="hp-step-text">
                    Add steps from the tool list and adjust parameters; for steps requiring a reference genome,
                    select the index (species/release).
                  </div>
                </div>
              </div>

              <div class="hp-step">
                <div class="hp-step-badge">Step 3</div>
                <div class="hp-step-main">
                  <div class="hp-step-title">Bind & Run</div>
                  <div class="hp-step-text">
                    Enter a sample name for each sample group and bind a source (upload/server) to every slot.
                    Once all bindings are complete, the task can be run.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 3 Core Concepts -->
          <section id="concepts" class="hp-card mb-3">
            <div class="hp-card-header">
              <div>
                <div class="hp-card-title-sm">3 · Core Concepts</div>
                <div class="hp-card-caption">Understanding these three points is enough</div>
              </div>
            </div>

            <div class="hp-grid2">
              <div class="hp-block">
                <div class="hp-block-title">① Input Key: type:name</div>
                <div class="hp-block-text">
                  Use <span class="hp-mono">type:variable</span> to represent an input slot,
                  making it easy to distinguish multiple inputs of the same type
                  (rep1/rep2, case/control).
                </div>
                <div class="hp-code">
                  fq:sample<br />
                  fq1:rep1 + fq2:rep1<br />
                  bam:sample<br />
                  bed:peaks
                </div>
              </div>

              <div class="hp-block">
                <div class="hp-block-title">② Step “in” Supports Multiple Inputs</div>
                <div class="hp-block-text">
                  <span class="hp-mono">in</span> is a multi-input mapping that can reference multiple input keys.
                  Sources can be either input or upstream steps.
                </div>
                <div class="hp-code">
                  in: {<br />
                  &nbsp;&nbsp;"fq1:rep1": from=input<br />
                  &nbsp;&nbsp;"fq2:rep1": from=input<br />
                  }
                </div>
              </div>
            </div>

            <div class="hp-note mt-2">
              <div class="hp-note-title">③ Sample Groups</div>
              <div class="hp-note-text">
                Step 3 uses “sample groups” to manage sample names and bindings for all input slots.
                Before submission, all sample names must be non-empty and unique.
              </div>
            </div>
          </section>

          <!-- 4 FAQ -->
          <section id="faq" class="hp-card mb-2">
            <div class="hp-card-header">
              <div>
                <div class="hp-card-title-sm">4 · FAQ & Notes</div>
                <div class="hp-card-caption">Most common pitfalls are here</div>
              </div>
            </div>

            <div class="hp-faq">
              <div class="hp-faq-item">
                <div class="hp-faq-q">Q: Why can’t Step 1 be modified after confirmation?</div>
                <div class="hp-faq-a">
                  A: The binding UI in Step 3 is generated based on the input structure from Step 1.
                  Changing it midway would cause mismatches in the binding layout.
                </div>
              </div>

              <div class="hp-faq-item">
                <div class="hp-faq-q">Q: How do I specify paired-end data?</div>
                <div class="hp-faq-a">
                  A: Use <span class="hp-mono">fq1:repX</span> + <span class="hp-mono">fq2:repX</span>
                  to represent a pair (with the same repX).
                </div>
              </div>

              <div class="hp-faq-item">
                <div class="hp-faq-q">Q: How do presets or share codes work?</div>
                <div class="hp-faq-a">
                  A: Paste them at the top of the Batch Builder page
                  (supports p: share codes / names / JSON).
                  The backend parses them and returns the initial_pool and steps.
                </div>
              </div>
            </div>

            <div class="hp-divider my-3"></div>
            <ul class="hp-list">
              <li>When parameters such as <span class="hp-mono">@RG</span> are involved, pay attention to quoting and escaping.</li>
              <li>For multi-sample batches, always ensure sample names are unique, or bindings may be overwritten.</li>
              <li>If “the page doesn’t change”, first check whether the API response contains initial_pool and steps.</li>
            </ul>
          </section>

          <div class="hp-footer mt-3">
            <div class="hp-footer-text">
              This page can be extended in the future with more preset examples,
              output contract documentation, index field explanations, and more.
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HelpPage",
  methods: {
    goStore() {
      this.$router.push({ path: "/store" }).catch(() => {});
    },
    goBatch() {
      this.$router.push({ path: "/batch" }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* =========================
   Help page: "productized" visuals for a static documentation page
   - Align with App.vue global background (do not paint another full-page background here)
   - Use a frosted/soft-glass card style with clearer hierarchy and spacing
   ========================= */
.help-page{
  min-height: 100vh;
  background: transparent; /* Let the global App background show through */
  color: rgba(15,23,42,0.92);
}

/* Top header: a light frosted header card (like a document cover) */
.hp-header{
  padding: 14px 14px 12px;
  border-radius: 16px;

  background: rgba(255,255,255,0.72); /* frosted white */
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 14px 40px rgba(15,23,42,0.08);

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.hp-title{
  font-size: 26px;
  font-weight: 950;
  letter-spacing: -0.02em;
}
.hp-subtitle{
  font-size: 13px;
  color: rgba(15,23,42,0.65);
  line-height: 1.6;
}

/* Tips bar: lighter, more "hint-like" */
.hp-hintbar{
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(15,23,42,0.03);
}
.hp-hint-badge{
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.16em;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,0.14);
  background: rgba(255,255,255,0.80);
  color: rgba(15,23,42,0.72);
}
.hp-hint-text{
  font-size: 12px;
  color: rgba(15,23,42,0.72);
}

/* =========================
   Generic cards: frosted white + rounded corners + soft shadow
   ========================= */
.hp-card{
  border-radius: 16px;
  background: rgba(255,255,255,0.72); /* frosted white */
  border: 1px solid rgba(15,23,42,0.08);
  box-shadow: 0 14px 40px rgba(15,23,42,0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  padding: 14px;
  position: relative;
}

/* A thin top accent line for hierarchy (replaces a heavy black top border) */
.hp-card::before{
  content:"";
  position:absolute;
  left: 14px;
  right: 14px;
  top: 0;
  height: 3px;
  border-radius: 999px;
  background: rgba(15,23,42,0.82);
  opacity: 0.9;
}

/* sticky nav card */
.hp-card--sticky{
  position: sticky;
  top: 16px;
}

.hp-card-header{
  display:flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(15,23,42,0.08);
  margin-bottom: 12px;
}
.hp-card-title-sm{
  font-size: 14px;
  font-weight: 950;
}
.hp-card-caption{
  font-size: 12px;
  color: rgba(15,23,42,0.60);
}

/* =========================
   Left navigation: document-like navigation
   ========================= */
.hp-nav{
  display:flex;
  flex-direction: column;
  gap: 8px;
}
.hp-nav-item{
  display:flex;
  align-items:center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 12px;

  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.62);
  color: rgba(15,23,42,0.82);
  text-decoration: none;
  font-size: 13px;
  font-weight: 800;

  transition: transform .12s ease, box-shadow .12s ease, background .12s ease, border-color .12s ease;
}
.hp-nav-item:hover{
  background: rgba(255,255,255,0.82);
  border-color: rgba(15,23,42,0.14);
  box-shadow: 0 10px 26px rgba(15,23,42,0.08);
  transform: translateY(-1px);
  text-decoration: none;
}

.hp-mini-title{
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(15,23,42,0.60);
}
.hp-mini-text{
  margin-top: 8px;
  font-size: 13px;
  color: rgba(15,23,42,0.76);
  line-height: 1.7;
}

/* =========================
   Overview tiles: light card grid
   ========================= */
.hp-grid4{
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.hp-tile{
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.62);
  padding: 12px;
}
.hp-tile-title{
  font-size: 13px;
  font-weight: 950;
  margin-bottom: 6px;
}
.hp-tile-text{
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.7;
}

/* steps */
.hp-steps{
  display:flex;
  flex-direction: column;
  gap: 10px;
}
.hp-step{
  display:flex;
  gap: 12px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.62);
  padding: 12px;
}
.hp-step-badge{
  width: 72px;
  height: 28px;
  border-radius: 999px;
  display:flex;
  align-items:center;
  justify-content:center;

  border: 1px solid rgba(15,23,42,0.16);
  font-weight: 950;
  font-size: 12px;
  background: rgba(255,255,255,0.82);
  color: rgba(15,23,42,0.82);
}
.hp-step-title{
  font-size: 13.5px;
  font-weight: 950;
}
.hp-step-text{
  margin-top: 6px;
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.7;
}
.hp-step-main{ flex: 1; min-width: 0; }

/* concepts */
.hp-grid2{
  display:grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.hp-block{
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.62);
  padding: 12px;
}
.hp-block-title{
  font-size: 13px;
  font-weight: 950;
  margin-bottom: 8px;
}
.hp-block-text{
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.7;
}

/* code: frosted panel + monospace */
.hp-code{
  margin-top: 10px;
  border-radius: 12px;
  border: 1px dashed rgba(15,23,42,0.22);
  background: rgba(255,255,255,0.82);
  padding: 10px 12px;

  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  color: rgba(15,23,42,0.68);
  line-height: 1.6;
}

.hp-note{
  margin-top: 10px;
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(15,23,42,0.03);
  padding: 12px;
}
.hp-note-title{ font-size: 13px; font-weight: 950; }
.hp-note-text{
  margin-top: 6px;
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.7;
}

/* inline monospace */
.hp-mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,0.12);
  padding: 1px 8px;
  background: rgba(255,255,255,0.82);
  color: rgba(15,23,42,0.72);
}

/* FAQ */
.hp-faq{
  display:flex;
  flex-direction: column;
  gap: 10px;
}
.hp-faq-item{
  border-radius: 14px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(255,255,255,0.62);
  padding: 12px;
}
.hp-faq-q{ font-weight: 950; font-size: 13px; }
.hp-faq-a{
  margin-top: 6px;
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.7;
}

.hp-divider{ border-top: 1px solid rgba(15,23,42,0.12); }
.hp-list{
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: rgba(15,23,42,0.72);
  line-height: 1.8;
}

.hp-footer-text{
  font-size: 12px;
  color: rgba(15,23,42,0.62);
  line-height: 1.7;
}

/* Responsive */
@media (max-width: 992px){
  .hp-card--sticky{ position: static; }
  .hp-grid4{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hp-grid2{ grid-template-columns: 1fr; }
}
</style>

